import React, { useEffect, useState } from 'react';

// Components
import { Button } from '@capintel/component-lib/button';
import { FormattedMessageV2 } from '@/views/components/i18n';
import { ReviewingInformation } from './components/ReviewingInformation';
import { SendToField } from './components/SendToField';
import { Typography } from '@capintel/component-lib/typography';
import { withErrorBoundary } from '@/views/components/errors';

// Enums
import { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';
import { OmniRiskProfileStatus } from '@/enums/OmniRiskProfiles';

// Libraries
import classNames from 'classnames';

// Types
import { Email } from '@/types/models/risk-profiles/omni-risk-profile-stats';
import { FormattedFullOmniRiskProfile } from '@/types/risk-profiles/omni/full-omni-risk-profile';

// Utils
import { DATADOG_ACTIONS } from '@/utils/3rd-party/datadogUtils';
import { copyStringToClipboard } from '@/utils/link';
import { formatMessage } from '@/utils/i18n';

type Props = {
  authorizeEmail: (email: string) => Promise<void>;
  className?: string;
  hasEnforcedContactEmail: boolean;
  initialEmail?: string;
  riskProfile: FormattedFullOmniRiskProfile;
  showRiskProfileDetails?: boolean;
  sharedEmails?: Email[];
};

const SendQuestionnaireBody: React.FC<Props> = ({
  authorizeEmail,
  className,
  hasEnforcedContactEmail,
  initialEmail,
  riskProfile,
  showRiskProfileDetails,
  sharedEmails = [],
}) => {
  const { omniFormUrl, status } = riskProfile;
  const [isLinkCopied, setIsLinkCopied] = useState<boolean>(false);

  // Copy link text change
  useEffect(() => {
    if (isLinkCopied) {
      const timer = setTimeout(() => setIsLinkCopied(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [isLinkCopied]);

  const handleCopyLink = async () => {
    let copyUrl = omniFormUrl;
    const isReview = status === OmniRiskProfileStatus.ClientReview || status === OmniRiskProfileStatus.ClientReviewUnsent;

    if (isReview) {
      copyUrl = `${omniFormUrl}/ri-summary`;
    }

    await copyStringToClipboard(copyUrl);
    setIsLinkCopied(true);
  };

  return (
    <>
      <div className={classNames(`send-questionnaire-content ${className}`)}>
        <div className="subtitle">
          <Typography as={ALLOWED_HTML_TAGS.P} color="primary" variant="bodySmRegular">
            <FormattedMessageV2 toLocalize="RiskProfileModal.SendQuestionnaire.Subtitle" />
          </Typography>
        </div>
        <SendToField
          authorizeEmail={authorizeEmail}
          hasEnforcedContactEmail={hasEnforcedContactEmail}
          initialEmail={initialEmail}
          riskProfile={riskProfile}
          sharedEmails={sharedEmails}
        />
        <ReviewingInformation
          authorizeEmail={authorizeEmail}
          riskProfile={riskProfile}
          sharedEmails={sharedEmails}
          showRiskProfileDetails={showRiskProfileDetails}
        />
      </div>
      <div className="send-questionnaire-footer">
        <Button
          variant="secondary"
          data-dd-action-name={DATADOG_ACTIONS.RISK_PROFILE.SEND_QUESTIONNAIRE.COPY_LINK}
          disabled={!sharedEmails.length || isLinkCopied}
          icon="copy"
          onClick={handleCopyLink}
        >
          {formatMessage(`RiskProfileModal.SendQuestionnaire.${isLinkCopied ? 'LinkCopied' : 'CopyLink'}`)}
        </Button>
      </div>
    </>
  );
};

export default withErrorBoundary(SendQuestionnaireBody);
